import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LoadingScreen from "./loadingScreen";
import { FaFilter, FaChevronDown, FaChevronUp, FaDownload } from "react-icons/fa";
import { fetchFaultData } from "../utils/firebase";
import "./styleSheets/FaultData.css";
import { Modal, TextField, FormControl, InputLabel, Select, MenuItem, Button, Checkbox } from "@mui/material";
import { saveAs } from "file-saver";

const FaultData = () => {

    const navigate = useNavigate();
    const { siteName, selectedDate } = useParams();

    const [faultData, setFaultData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFilterVisible, setIsFilterVisible] = useState(true);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportFileName, setExportFileName] = useState("FaultData");
    const [exportFormat, setExportFormat] = useState("csv");

    const [selectedLevels, setSelectedLevels] = useState(["Level 1", "Level 2", "Level 3"]);

    const [availableCategories, setAvailableCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);

    const [selectedFaultIDs, setSelectedFaultIDs] = useState([]);
    const [availableFaultIDs, setAvailableFaultIDs] = useState([]);


    // Filtering state
    const [levelFilter, setLevelFilter] = useState("All");
    const [categoryFilter, setCategoryFilter] = useState("All");

    console.log("siteName:", siteName);
    console.log("selectedDate:", selectedDate);


    useEffect(() => {

        const fetchData = async () => {
            try {
                const faults = await fetchFaultData(siteName, selectedDate);
                if (!faults) throw new Error("No fault data available.");

                console.log("Fetched Faults:", faults);

                const sortedFaults = Object.values(faults).sort((a, b) => {
                    const faultIdA = a.ID || "Unknown";
                    const faultIdB = b.ID || "Unknown";

                    const serialNumA = parseInt(faultIdA.split("-")[1], 10) || 0;
                    const serialNumB = parseInt(faultIdB.split("-")[1], 10) || 0;

                    return serialNumA - serialNumB;
                });

                console.log("Sorted Fault Data:", sortedFaults);

                setFaultData(sortedFaults);

                const faultIdMap = {};
                sortedFaults.forEach((fault) => {
                    const faultId = fault.ID ? fault.ID.trim() : "Unknown";
                    const category = fault.category ? fault.category.trim() : "Unknown";

                    if (!faultIdMap[faultId]) {
                        faultIdMap[faultId] = category;
                    }
                });

                const formattedFaultIDs = Object.entries(faultIdMap).map(
                    ([faultId, category]) => `${faultId} - ${category}`
                );

                setAvailableFaultIDs(formattedFaultIDs);

                setLoading(false);
            } catch (error) {
                console.error("Error fetching fault data:", error);
                setError("Failed to fetch fault data");
                setLoading(false);
            }
        };


        fetchData();
    }, [siteName, selectedDate]);

    useEffect(() => {
        if (faultData.length === 0) return;

        // Extract unique categories
        const uniqueCategories = [...new Set(faultData.map(fault => fault.category))];

        setAvailableCategories(uniqueCategories);
        setSelectedCategories(uniqueCategories); // Default: Select All
    }, [faultData]);

    const filteredFaults = faultData.filter((fault) => {
        return (
            (selectedLevels.length === 0 || selectedLevels.includes(fault.level)) &&
            (selectedCategories.length === 0 || selectedCategories.includes(fault.category)) &&
            (selectedFaultIDs.length === 0 ||
                selectedFaultIDs.some(id => id.split(" - ")[0] === fault.ID))
        );
    });


    const handleExport = () => {
        let exportData;
        if (exportFormat === "csv") {
            exportData = filteredFaults.map(fault =>
                `${fault.faultId},${fault.level},${fault.category},${fault.location},${fault.latitude},${fault.longitude}`
            );
            exportData.unshift("Fault ID,Level,Category,Location,Latitude,Longitude");
            exportData = exportData.join("\n");
        } else if (exportFormat === "json") {
            exportData = JSON.stringify(filteredFaults, null, 2);
        } else if (exportFormat === "sql") {
            exportData = `INSERT INTO faults (faultId, level, category, location, latitude, longitude) VALUES `;
            exportData += filteredFaults.map(fault =>
                `('${fault.faultId}', '${fault.level}', '${fault.category}', '${fault.location}', ${fault.latitude}, ${fault.longitude})`
            ).join(",\n") + ";";
        }

        const blob = new Blob([exportData], { type: exportFormat === "json" ? "application/json" : "text/plain" });
        saveAs(blob, `${exportFileName}.${exportFormat}`);

        setExportModalOpen(false);
    };

    if (loading) return <LoadingScreen />;
    if (error) return <p>{error}</p>;

    return (
        <div className="page-layout">
            {/* Filter Section */}
            <div className="filter-section">
                <h2>Table Options</h2>

                <div className="filter-controls">
                    <button className="export-button" onClick={() => setExportModalOpen(true)}>
                        <FaDownload /> Export Table
                    </button>

                    <FormControl sx={{ minWidth: 150, maxWidth: 140 }} variant="outlined">
                        <InputLabel shrink>Level</InputLabel>
                        <Select
                            multiple
                            label="level-label"
                            value={selectedLevels}
                            onChange={(e) => setSelectedLevels(e.target.value)}
                            renderValue={(selected) => selected.join(", ")}
                        >
                            {["Level 1", "Level 2", "Level 3"].map((level) => (
                                <MenuItem key={level} value={level}>
                                    <Checkbox checked={selectedLevels.includes(level)} />
                                    {level}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Category Filter (Multi-Select Checkbox) */}
                    <FormControl sx={{ minWidth: 150, maxWidth: 200 }} variant="outlined">
                        <InputLabel shrink>Category</InputLabel>
                        <Select
                            multiple
                            label="category-label"
                            value={selectedCategories}
                            onChange={(e) => setSelectedCategories(e.target.value)}
                            renderValue={(selected) => selected.join(", ")}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        overflowY: "auto",
                                    },
                                },
                            }}
                        >
                            {availableCategories.map((category) => (
                                <MenuItem key={category} value={category}>
                                    <Checkbox checked={selectedCategories.includes(category)} />
                                    {category}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* faultID  Filter */}
                    <FormControl sx={{ minWidth: 200, maxWidth: 250 }} variant="outlined">
                        <InputLabel shrink>Fault ID</InputLabel>
                        <Select
                            multiple
                            displayEmpty
                            label="FaultID-label"
                            value={selectedFaultIDs}
                            onChange={(e) => setSelectedFaultIDs(e.target.value)}
                            renderValue={(selected) => selected.join(", ")}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        overflowY: "auto",
                                    },
                                },
                            }}
                        >
                            {availableFaultIDs.map((faultId) => (
                                <MenuItem key={faultId} value={faultId}>
                                    <Checkbox checked={selectedFaultIDs.includes(faultId)} />
                                    {faultId}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </div>

            {/* Fault Table */}
            <div className="fault-data-table">
                <h3>Fault Data</h3>
                <table>
                    <thead>
                        <tr>
                            <th>Fault ID</th>
                            <th>Level</th>
                            <th>Category</th>
                            <th>Location</th>
                            <th>Latitude</th>
                            <th>Longitude</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredFaults.map((fault) => (
                            <tr key={fault.faultId}>
                                <td>{fault.ID}</td>
                                <td>{fault.level}</td>
                                <td>{fault.category}</td>
                                <td>{fault.location}</td>
                                <td>{fault.latitude}</td>
                                <td>{fault.longitude}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Export Modal */}
            <Modal open={exportModalOpen} onClose={() => setExportModalOpen(false)}>
                <div className="export-modal">
                    <h3>Export Data</h3>
                    <TextField label="File Name" variant="outlined" value={exportFileName} onChange={(e) => setExportFileName(e.target.value)} />
                    <FormControl sx={{ width: "100%", mt: 2 }}>
                        <InputLabel id="format-label">Format</InputLabel>
                        <Select
                            labelId="format-label"
                            value={exportFormat}
                            onChange={(e) => setExportFormat(e.target.value)}
                            label="Format"
                            sx={{
                                backgroundColor: "white",
                                "&.MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#285335",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#0f1f14",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#09130c",
                                    },
                                },
                            }}
                        >
                            <MenuItem value="csv">CSV</MenuItem>
                            <MenuItem value="json">JSON</MenuItem>
                            <MenuItem value="sql">SQL</MenuItem>
                        </Select>
                    </FormControl>
                    <Button onClick={handleExport} variant="contained" sx={{ backgroundColor: "#285335", color: "white" }}>Export</Button>
                </div>
            </Modal>
        </div>
    );
};

export default FaultData;
